import type { PlayerRole, Vec3 } from './types.js';
import type { HazardPhase, HazardType, StageOutcome, TargetConfiguration, TargetState } from './types.js';

// Client -> Server message types
export enum ClientMessage {
  DriverInput = 'driver_input',
  PassengerInput = 'passenger_input',
  PlayerReady = 'player_ready',
  SelectRole = 'select_role',
  SkipPresentation = 'skip_presentation',
}

export type RoleChoice = 'driver' | 'passenger' | 'random';

export interface SelectRolePayload {
  role: RoleChoice;
}

// Server -> Client message types
export enum ServerMessage {
  RoleAssigned = 'role_assigned',
  CountdownTick = 'countdown_tick',
  GameStart = 'game_start',
  StageBriefing = 'stage_briefing',
  ObjectiveChanged = 'objective_changed',
  BeatRecovery = 'beat_recovery',
  TargetContact = 'target_contact',
  TargetStateChanged = 'target_state_changed',
  TargetConfigurationChanged = 'target_configuration_changed',
  TargetComponentBroken = 'target_component_broken',
  HazardWarning = 'hazard_warning',
  HazardCommitted = 'hazard_committed',
  HazardResolved = 'hazard_resolved',
  StageDebrief = 'stage_debrief',
  CampaignComplete = 'campaign_complete',
  ShotFired = 'shot_fired',
  HitConfirm = 'hit_confirm',
  EnemyDestroyed = 'enemy_destroyed',
  EnemyTelegraph = 'enemy_telegraph',
  EnemyFired = 'enemy_fired',
  ObstacleDestroyed = 'obstacle_destroyed',
  BossPhaseChange = 'boss_phase_change',
  LevelComplete = 'level_complete',
  GameOver = 'game_over',
  PlayerDisconnected = 'player_disconnected',
  PlayerReconnected = 'player_reconnected',
}

export type GunId = 'driver' | 'left' | 'right';

export interface ShotFiredPayload {
  shooter: string;       // sessionId, so clients skip re-rendering their own shots
  gun: GunId;
  origin: Vec3;
  end: Vec3;             // hit point, or max-range point on a miss
  hit: boolean;
}

export interface HitConfirmPayload {
  enemyId: string;
  damage: number;
  destroyed: boolean;
  point: Vec3;
  shooter: string;
  componentId?: string;
  componentBroken?: boolean;
}

export interface EnemyDestroyedPayload {
  enemyId: string;
  point: Vec3;
  score: number;
  combo: number;
  byRole: PlayerRole;
}

export interface EnemyTelegraphPayload {
  enemyId: string;
  target: Vec3;
  targetT: number;
  durationMs: number;
}

export interface ObstacleDestroyedPayload {
  obstacleId: string;
  point: Vec3;
  collided: boolean;      // true: the car hit it; false: it was shot
  vehicleDamage: number;  // 0 when shot
  score: number;          // 0 on collision
  repairAmount?: number;  // >0 when a roadside repair cache was shot
}

export interface EnemyFiredPayload {
  enemyId: string;
  damage: number;
  origin: Vec3;
  target: Vec3;
  hit: boolean;
}

export interface BossPhaseChangePayload {
  phase: number; // 1..3
}

export interface StageBriefingPayload {
  level: number;
  stageId: string;
  title: string;
  location: string;
  briefing: string;
  targetTitle: string;
  targetBounty: number;
  timeLimitSeconds: number;
}

export interface ObjectiveChangedPayload {
  beatId: string;
  beatTitle: string;
  objective: string;
}

export interface BeatRecoveryPayload {
  amount: number;
  health: number;
}

export interface TargetStatePayload {
  targetId: string;
  state: TargetState;
}

export interface TargetConfigurationPayload {
  targetId: string;
  configuration: TargetConfiguration;
  label: string;
}

export interface TargetComponentBrokenPayload {
  targetId: string;
  componentId: string;
  label: string;
  byRole: PlayerRole;
}

export interface HazardPayload {
  id: string;
  hazardType: HazardType;
  phase: HazardPhase;
  lateralOffset: number;
  label: string;
  hit?: boolean;
  nearMiss?: boolean;
  damage?: number;
}

export interface StageDebriefPayload {
  level: number;
  stageId: string;
  outcome: StageOutcome;
  score: number;
  elapsedSeconds: number;
  takedowns: number;
  accuracy: number;
  componentsBroken: number;
  targetBounty: number;
  campaignComplete: boolean;
  combatScore: number;
  bonusScore: number;
  penalties: number;
  nearMisses: number;
  vehicleHealth: number;
  grade: 'S' | 'A' | 'B' | 'C' | 'D' | 'F';
}
